import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useSelector, useDispatch } from 'react-redux';
import { actions } from 'store/conditionQueryTemplate';
import { ToolbarV2, TableComponentV2 } from 'components';
import filterHelpers from 'store/helpers/filterHelpers';
import { conditionQueriesTableTitles } from 'constants.js';
import { useHistory } from 'react-router-dom';
import { produce } from 'immer';
import { mapExpressionFromResposne } from 'store/helpers';
import { resetListsData } from 'store/lists/actions';
import { deleteConditionQuery, resetSnackbar, setDefault } from 'store/conditionQueryTemplate/actions';
import PopupModal from 'views/UserMessaging/customModalContent/popupModal';
import UserMessageSnackbar from 'views/UserMessaging/customModalContent/userMessageSnackbar';
import CopyTemplateModal from 'components/Template/CopyTemplateModal';

const {
  getPaginatedConditionQueryTemplates,
  unsetCurrentConditionQueryTemplate,
  setCurrentConditionQueryTemplate,
  updateConditionQueryFilters,
  resetConditionQueryFilters
} = actions;

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  content: {
    marginTop: theme.spacing(2)
  }
}));

const ConditionQueries = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    onClickAdd,
    onClickEdit,
    isTemplateReadOnly
  } = filterHelpers();
  const conditionQueries = useSelector(
    store => store.conditionQueryTemplate.paginatedList
  );
  const filters = useSelector(store => store.conditionQueryTemplate.filters);
  const isLoading = useSelector(store => store.conditionQueryTemplate.gettingPaginated);
  const productNames = useSelector(
    store => store.events.supportedEventTypes.productNames
  );
  const productCategories = useSelector(
    store => store.events.supportedEventTypes.productCategories
  );
  const isDeleting = useSelector(store => store.conditionQueryTemplate.isDeleting);
  const deleteSucceeded = useSelector(store => store.conditionQueryTemplate.deleteSucceeded);
  const snackbarInfo = useSelector(store => store.conditionQueryTemplate.snackbarInfo);

  const optionsToFilterTemplate = [
    {
      name: 'searchScopes',
      searchScope: 'TENANT DEFAULT',
      value: 'tenant',
      checked: false
    },
    {
      name: 'searchScopes',
      searchScope: 'PROPERTY LEVEL',
      value: 'property',
      checked: false
    }
  ];
  const optionsToFilterProductNames = productNames
    ? productNames.map(productName => {
      return { name: 'productName', value: productName, checked: false };
    })
    : [];
  const optionsToFilterProductCategories = productCategories
    ? productCategories.map(productCategory => {
      return {
        name: 'productCategory',
        value: productCategory,
        checked: false
      };
    })
    : [];

  const [showAlert, setShowAlert] = useState(false);
  const [currentUuid, setCurrentUuid] = useState('');
  const [openCopyTemplateModal, setOpenCopyTemplateModal] = useState(false);

  useEffect(() => {
    dispatch(resetSnackbar());
    dispatch(getPaginatedConditionQueryTemplates({ force: true }));
    dispatch(resetListsData());
  }, []);

  useEffect(() => {
    setShowAlert(isDeleting);
    if (deleteSucceeded) {
      dispatch(getPaginatedConditionQueryTemplates({ force: true }));
    }
  }, [isDeleting]);

  const handleImportClick = () => {
    setOpenCopyTemplateModal(true);
  };

  const getButtonsGroup = () => [
    {
      name: 'Add Condition Query',
      action: () => onClickAdd(unsetCurrentConditionQueryTemplate(), `/condition-queries/details`)
    },
    {
      name: 'Import',
      action: () => handleImportClick()
    }
  ];

  const getActionButtons = ConditionQueryTemplate => [
    {
      name: isTemplateReadOnly(ConditionQueryTemplate, isLoading)
        ? 'View Query'
        : 'Edit Query',
      action: ['ACTION', () => onEdit(ConditionQueryTemplate)],
      ...(isTemplateReadOnly(ConditionQueryTemplate, isLoading) && {
        tooltipPlacement: 'left-start',
        tooltipTitle: "Tenant Default query can be edited only with 'ALL' property"
      })
    },
    {
      name: 'Copy Query',
      action: ['ACTION', () => onCopy(ConditionQueryTemplate.uuid)]
    },
    {
      name: 'Delete Query',
      action: ['ACTION', () => onDelete(ConditionQueryTemplate.uuid)],
      ...(isTemplateReadOnly(ConditionQueryTemplate, isLoading) && {
        isDisabled: true,
        tooltipPlacement: 'left-start',
        tooltipTitle: "Tenant Default query can be edited only with 'ALL' property"
      })
    }
  ];

  const handleClose = () => {
    setShowAlert(false);
    setCurrentUuid('');
  };

  const getButtonGroup = () =>
    [
      { title: 'Delete', action: () => dispatch(deleteConditionQuery(currentUuid)) },
      { title: 'Cancel', action: () => handleClose() }
    ];

  const filterOptions = {
    filters: [
      {
        options: optionsToFilterTemplate.map(option => {
          if (filters?.filterTemplates?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Hierarchy',
        values: filters?.filterTemplates,
        onChange: templates => dispatch(updateConditionQueryFilters({ filterTemplates: templates, pageNo: 0 }))
      },
      {
        options: optionsToFilterProductNames.map(option => {
          if (filters?.filterProductNames?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Product Name',
        values: filters?.filterProductNames,
        onChange: productNames =>
          dispatch(updateConditionQueryFilters({ filterProductNames: productNames, pageNo: 0 }))
      },
      {
        options: optionsToFilterProductCategories.map(option => {
          if (filters?.filterProductCategories?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Product Category',
        values: filters?.filterProductCategories,
        onChange: productCategories =>
          dispatch(updateConditionQueryFilters({ filterProductCategories: productCategories, pageNo: 0 }))
      }
    ],
    resetFilters: () => dispatch(resetConditionQueryFilters())
  };

  const searchOptions = {
    searchPlaceholder: 'Search Condition Queries',
    searchAction: (payload) => dispatch(updateConditionQueryFilters(payload)),
    searchKey: 'searchInput',
    searchValue: filters?.searchInput || ''
  };

  const onEdit = row =>
    onClickEdit(
      [unsetCurrentConditionQueryTemplate(), setCurrentConditionQueryTemplate(row)],
      `/condition-queries/details/${row.uuid}`
    );
  const onCopy = id => {
    let template = Object.assign(
      {},
      conditionQueries.find(t => t.uuid === id)
    );
    delete template.uuid;
    template._isCopy = true;
    template.name = template.name + '-copy';
    template = produce(template, draftTemplate => {
      draftTemplate.conditionExpression = mapExpressionFromResposne(
        draftTemplate?.conditionExpression || {}
      );
      if (draftTemplate.hasOwnProperty('_hasSetTemplate')) {
        draftTemplate._hasSetTemplate = true;
      }
    });
    dispatch(setCurrentConditionQueryTemplate(template));
    history.push(`/condition-queries/details`);
  };

  const onDelete = id => {
    setShowAlert(true);
    setCurrentUuid(id);
  };

  return (
    <div className={classes.root}>
      <ToolbarV2
        searchOptions={searchOptions}
        filterOptions={filterOptions}
        buttonsGroup={getButtonsGroup()}
        action={getPaginatedConditionQueryTemplates({ force: true })}
      />
      <div className={classes.content}>
        <TableComponentV2
          isLoading={isLoading}
          tableTitles={conditionQueriesTableTitles}
          onDoubleClickRow={row => onEdit(row)}
          actionButtonOptions={row => getActionButtons(row)}
          filteredRows={conditionQueries}
          action={getPaginatedConditionQueryTemplates({ force: true })}
          filterValues={filters}
          filterAction={(payload) => dispatch(updateConditionQueryFilters(payload))}
        />
      </div>
      <PopupModal
        isOpen={showAlert}
        handleClose={handleClose}
        title='Delete Condition query'
        severity='warning'
        content='Are you sure you want to delete this condition query?'
        buttonGroup={getButtonGroup()} />
      <UserMessageSnackbar
        openSnackbar={snackbarInfo.show}
        message={snackbarInfo.message}
        severity={snackbarInfo.severity}
        onClose={() => dispatch(resetSnackbar())}
      />
      {openCopyTemplateModal && (
        <CopyTemplateModal
          isOpen={openCopyTemplateModal}
          closeModal={() => setOpenCopyTemplateModal(false)}
        />
      )}
    </div>
  );
};

export default ConditionQueries;